import { Shift } from '../types';
import { sMin, eMin, clockLabel } from './shiftTime';

// 時間帯ごとの充足判定。
// 募集枠の必要人数は「その枠の時間中、どの時刻にも○名いる」という意味で扱う。
// 30分ごとに、必要人数（同じ日・同じポジションで重なる枠は合算）と、その時刻に入っている人数を比べる。
// 例：10:00〜17:00 必要1名の枠に、10:00〜13:00 のAさんと 13:00〜17:00 のBさんが入れば充足（2人で1名分）。

export const COVERAGE_STEP = 30;
export const DEFAULT_MIN_SHIFT_MINUTES = 120;

export interface CoverageGap { start: number; end: number; missing: number }

// 時刻は営業日（開始日）の0時からの分で扱う（夜をまたぐ枠は 24:00 以降になる）
export const fmtMinutes = clockLabel;
export const fmtGap = (g: { start: number; end: number }) => `${fmtMinutes(g.start)}〜${fmtMinutes(g.end)}`;

const isWorking = (s: Shift) => !!s.staffId && s.type !== 'vacancy' && s.type !== 'off';
// 欠員枠（シフトを外した・欠勤でできた枠）は、その枠に割り当てた人（vacancyId）だけで埋まったかを判定する。
// 通常の募集枠には、欠員枠の補充で入った人は数えない。
const isAbsenceSlot = (v: Shift) => v.label === '欠員';
const sameGroup = (a: Shift, v: Shift) => a.store === v.store && a.positionId === v.positionId && a.start.slice(0, 10) === v.start.slice(0, 10);

/**
 * 募集枠 v の時間内で、人数が足りない区間を返す。
 * 重なる枠が複数ある時刻は、開始の早い枠から順に人を数え、残りを後の枠の不足にする（同じ不足を二重に数えない）。
 */
export const vacancyGaps = (v: Shift, vacancies: Shift[], assigned: Shift[]): CoverageGap[] => {
  const vs = sMin(v); const ve = eMin(v);
  const absence = isAbsenceSlot(v);
  const group = absence ? [v] : vacancies.filter(x => x.type === 'vacancy' && !isAbsenceSlot(x) && sameGroup(x, v))
    .sort((a, b) => a.start.localeCompare(b.start) || a.id.localeCompare(b.id));
  const absenceIds = new Set(vacancies.filter(isAbsenceSlot).map(x => x.id));
  const people = assigned.filter(a => isWorking(a) && sameGroup(a, v) && (absence ? a.vacancyId === v.id : !(a.vacancyId && absenceIds.has(a.vacancyId))))
    .map(a => { const s = sMin(a); return { s, e: eMin(a) }; });
  const gaps: CoverageGap[] = [];
  for (let t = vs; t < ve; t += COVERAGE_STEP) {
    const mid = t + COVERAGE_STEP / 2;
    const have = people.filter(p => p.s <= mid && p.e > mid).length;
    let before = 0;
    for (const x of group) {
      const xs = sMin(x); const xe = eMin(x);
      if (!(xs <= mid && xe > mid)) continue;
      if (x.id === v.id) break;
      before += x.headcount || 1;
    }
    const need = v.headcount || 1;
    const missing = Math.max(0, Math.min(need, before + need - have));
    const last = gaps[gaps.length - 1];
    if (missing > 0 && last && last.end === t && last.missing === missing) last.end = Math.min(ve, t + COVERAGE_STEP);
    else if (missing > 0) gaps.push({ start: t, end: Math.min(ve, t + COVERAGE_STEP), missing });
  }
  return gaps;
};

export const maxMissing = (gaps: CoverageGap[]) => gaps.reduce((m, g) => Math.max(m, g.missing), 0);
export const missingMinutes = (gaps: CoverageGap[]) => gaps.reduce((acc, g) => acc + (g.end - g.start) * g.missing, 0);
export const requiredMinutes = (v: Shift) => { const s = sMin(v); return (eMin(v) - s) * (v.headcount || 1); };
export const gapsLabel = (gaps: CoverageGap[]) => gaps.map(g => `${fmtGap(g)} ${g.missing}名`).join('、');

/** 区間 [os, oe) のうち、不足区間と重なる分（人数×分ではなく、時間の長さ） */
export const gapOverlap = (os: number, oe: number, gaps: CoverageGap[]) =>
  gaps.reduce((acc, g) => acc + Math.max(0, Math.min(oe, g.end) - Math.max(os, g.start)), 0);

/**
 * 入れる時間 [os, oe)（希望と枠の重なり）を、不足している時間に合わせて詰める。
 * 不足区間の最初から最後までに縮め、最短勤務時間に満たない場合は入れる時間の範囲で延ばす。
 */
export const fitToGaps = (os: number, oe: number, gaps: CoverageGap[], minLen: number): { start: number; end: number } => {
  const hit = gaps.filter(g => g.start < oe && g.end > os);
  if (hit.length === 0) return { start: os, end: oe };
  let s = Math.max(os, hit[0].start); let e = Math.min(oe, hit[hit.length - 1].end);
  if (e - s < minLen) { e = Math.min(oe, s + minLen); if (e - s < minLen) s = Math.max(os, e - minLen); }
  return { start: s, end: e };
};

/**
 * 表示用：同じ日・同じポジションの割り当てを、どの募集枠の人として表示するか振り分ける。
 * 開始の早い枠から順に、時間が重なる人を必要人数まで入れ、残りは重なりが最も長い枠に入れる（同じ人を2つの枠に表示しない）。
 */
export const allocateToVacancies = (group: Shift[], assigned: Shift[]): Map<string, Shift[]> => {
  const out = new Map<string, Shift[]>(group.map(v => [v.id, []]));
  const absence = group.filter(isAbsenceSlot);
  const normal = group.filter(v => !isAbsenceSlot(v)).sort((a, b) => a.start.localeCompare(b.start) || a.id.localeCompare(b.id));
  const people = assigned.filter(isWorking).sort((a, b) => a.start.localeCompare(b.start) || b.end.localeCompare(a.end));
  for (const a of people) {
    const ab = absence.find(v => a.vacancyId === v.id);
    if (ab) { out.get(ab.id)!.push(a); continue; }
    const as = sMin(a); const ae = eMin(a);
    const overlaps = normal.map(v => { const s = sMin(v); const e = eMin(v); return { v, ov: Math.min(e, ae) - Math.max(s, as) }; }).filter(x => x.ov > 0);
    if (overlaps.length === 0) continue;
    const free = overlaps.find(x => out.get(x.v.id)!.length < (x.v.headcount || 1));
    const pick = free || overlaps.sort((x, y) => y.ov - x.ov)[0];
    out.get(pick.v.id)!.push(a);
  }
  return out;
};

/** 不足している時間に入ってもらう場合の勤務時間（追加募集・打診・応募で使う）。不足がなければ null */
export const openTimeOf = (v: Shift, gaps: CoverageGap[], minLen: number) => {
  if (gaps.length === 0) return null;
  const vs = sMin(v);
  return fitToGaps(vs, eMin(v), gaps, minLen);
};
